import Cookies from 'js-cookie';
import ArrowIcon from '@/components/icons/sidebar/Arrow';
import NavigationDropdown from './components/sidebar/NavigationDropdown';
import Logo from '@/components/icons/Logo';
import { MenuIcon, XIcon } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/core/global-store';
import { toggleSidebar, toggleSidebarOnMobile } from '@/core/global-store/features/ui/uiSlice';
import { cn } from '@/lib/utils';
import { navigation, navigationBottom } from '@/core/global-data/SidebarData';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const Sidebar = () => {
    const lng = Cookies.get('i18next');
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const isSidebarOpen = useAppSelector((state) => state.ui.isSidebarOpen);
    const isSidebarOpenOnMobile = useAppSelector((state) => state.ui.isSidebarOpenOnMobile);
    const [activeTab, setActiveTab] = useState<string>(t('dashboard'));
    const [secondaryActiveTab, setSecondaryActiveTab] = useState<string>('');
    const navigate = useNavigate();

    useEffect(() => {
        // Handle sidebar active tabs from URL updates
        const currentPath = location.pathname;
        let mainNavItem;

        if (currentPath.length > 1) {
            mainNavItem = navigation.slice(1).find((item) => {
                return currentPath.replace(/\//g, '').includes(item.to.replace(/\//g, ''));
            });
        } else {
            mainNavItem = navigation[0];
        }

        const bottomNavItem = navigationBottom.find((item) => item.to === currentPath);

        if (mainNavItem) {
            setActiveTab(t(mainNavItem.name));
            if (mainNavItem.navigation) {
                const secondNavItem = mainNavItem.navigation.find((item) => item.to === currentPath);
                setSecondaryActiveTab(secondNavItem ? t(secondNavItem.name) : '');
            }
        } else if (bottomNavItem) {
            setActiveTab(t(bottomNavItem.name));
        }
    }, [location.pathname, t]);

    const toggle = () => dispatch(toggleSidebar());
    const toggleMobile = () => dispatch(toggleSidebarOnMobile());
    return (
        <>
            {/* Mobile menu button */}
            <button
                className="md:hidden fixed top-4 end-4 z-50 p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                onClick={() => toggleMobile()}
            >
                {isSidebarOpenOnMobile ? <XIcon className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
            </button>

            {/* Sidebar */}
            <aside
                className={cn(
                    'fixed h-screen start-0 z-40 bg-primery-blue-500 shadow-lg transition-all duration-300 ease-in-out md:translate-x-0 md:sticky md:top-0',
                    {
                        'w-[240px]': isSidebarOpen,
                        'w-20': !isSidebarOpen,
                        'translate-x-0': isSidebarOpenOnMobile,
                        'translate-x-full': !isSidebarOpenOnMobile && lng === 'ar',
                        '-translate-x-full': !isSidebarOpenOnMobile && lng !== 'ar',
                    },
                )}
            >
                <div className="flex flex-col h-full text-white-blue-50">
                    {/* Logo and Toggle */}
                    <div
                        className={cn('flex items-center justify-between h-8 my-4', {
                            'mx-auto': !isSidebarOpen,
                            'ms-6': isSidebarOpen,
                        })}
                    >
                        <span className="cursor-pointer" onClick={() => navigate('/')}>
                            {isSidebarOpen ? <Logo /> : <Logo width="50" />}
                        </span>
                        <button
                            onClick={() => toggle()}
                            className={cn(
                                'hidden md:block hover:text-primery-blue-500 p-2 hover:bg-gray-100 rounded-lg transition-transform',
                                {
                                    'rotate-180 !hidden': !isSidebarOpen,
                                },
                            )}
                        >
                            <ArrowIcon />
                        </button>
                    </div>

                    {/* Navigation Items */}
                    <nav className="flex-1 ms-6 font-medium text-base leading-5 mt-2 overflow-y-auto custom-scrollbar">
                        <button
                            onClick={() => toggle()}
                            className={cn(
                                'hidden rotate-180 hover:text-primery-blue-500 md:flex w-full mb-1 items-center justify-end py-4 px-2 hover:bg-gray-100 rounded-e-lg transition-transform',
                                {
                                    'rotate-180 !hidden': isSidebarOpen,
                                },
                            )}
                        >
                            <ArrowIcon />
                        </button>
                        {navigation.map((item, index) => {
                            const Icon = activeTab === t(item.name) ? item.activeIcon : item.icon;
                            return (
                                <div key={index}>
                                    <NavigationDropdown
                                        to={item.to}
                                        Icon={Icon}
                                        activeTab={activeTab}
                                        name={item.name}
                                        isSidebarOpen={isSidebarOpen}
                                        setActiveTab={setActiveTab}
                                        secondaryActiveTab={secondaryActiveTab}
                                        setSecondaryActiveTab={setSecondaryActiveTab}
                                        navigation={item.navigation}
                                    />
                                </div>
                            );
                        })}
                    </nav>

                    <nav
                        className={cn('mx-5 mt-auto mb-8 font-medium text-base leading-5', {
                            'me-10 ms-0': !isSidebarOpen,
                        })}
                    >
                        {navigationBottom.map((item) => {
                            const Icon = activeTab === t(item.name) ? item.activeIcon : item.icon;
                            return (
                                <button
                                    key={t(item.name)}
                                    onClick={() => setActiveTab(t(item.name))}
                                    className={cn(
                                        'w-full flex items-center justify-start hover:text-primery-blue-700 rounded-lg mb-1 p-2',
                                        {
                                            'bg-white-blue-50 text-primery-blue-500': activeTab === t(item.name),
                                            'hover:bg-white-blue-300': activeTab !== t(item.name),
                                            'ms-1': !isSidebarOpen,
                                        },
                                    )}
                                >
                                    <Icon
                                        className={`w-6 h-6 flex items-center justify-center ${isSidebarOpen ? 'me-2' : 'mx-auto'}`}
                                    />
                                    <span className={`${!isSidebarOpen && 'hidden'} text-start`}>{t(item.name)}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
